import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Download, Upload, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { downloadBlob } from '../../lib/downloadFile';
import cloudAssetService, {
  CloudImportValidation,
} from '../../services/cloudAssetService';

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

/**
 * Bulk import (User Story 83).
 *
 * Two legs, deliberately: validate returns the full error list and writes
 * nothing, so a file with one bad row can be fixed before anything lands.
 * Confirm re-resolves each row server-side rather than trusting this preview.
 */
export const CloudImportDialog: React.FC<Props> = ({
  open,
  onClose,
  onImported,
}) => {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [validation, setValidation] = useState<CloudImportValidation | null>(
    null,
  );
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setFileName('');
    setValidation(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleTemplate = async () => {
    try {
      const blob = await cloudAssetService.downloadImportTemplate();
      downloadBlob(blob, 'cloud-resource-import-template.xlsx');
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Failed to download template', 'error');
    }
  };

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setBusy(true);
    try {
      const result = await cloudAssetService.validateImport(file);
      setValidation(result);
    } catch (error: any) {
      setValidation(null);
      showToast(error?.friendlyMessage || 'Could not read that file', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!validation?.rows.length) return;
    setBusy(true);
    try {
      const result = await cloudAssetService.confirmImport(validation.rows);
      showToast(
        `Import complete — ${result.created} created, ${result.updated} updated`,
        'success',
      );
      onImported();
      handleClose();
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Import failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const hasErrors = Boolean(validation?.errors.length);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="flex flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl sm:p-0">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle>Import Cloud Resources</DialogTitle>
          <DialogDescription>
            Download the template, fill it in, and upload it. Environments, resource
            types and departments must already exist — the import reports unknown
            values rather than creating them.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleTemplate}>
              <Download className="mr-2 h-4 w-4" />
              Download Template
            </Button>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            >
              <Upload className="mr-2 h-4 w-4" />
              {fileName || 'Choose File'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={handleFile}
            />
          </div>

          {busy && !validation && (
            <p className="text-sm text-muted-foreground">Validating…</p>
          )}

          {validation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { label: 'Rows', value: validation.summary.total },
                  { label: 'Valid', value: validation.summary.valid },
                  { label: 'To create', value: validation.summary.toCreate },
                  { label: 'To update', value: validation.summary.toUpdate },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-md border p-3">
                    <div className="text-xs text-muted-foreground">
                      {stat.label}
                    </div>
                    <div className="text-lg font-semibold">{stat.value}</div>
                  </div>
                ))}
              </div>

              {hasErrors ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    {validation.errors.length} problem(s) found — these rows will
                    be skipped
                  </div>
                  <div className="max-h-64 overflow-y-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">Row</TableHead>
                          <TableHead>Field</TableHead>
                          <TableHead>Problem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {validation.errors.map((error, index) => (
                          <TableRow key={index}>
                            <TableCell>{error.row}</TableCell>
                            <TableCell className="font-mono text-xs">
                              {error.field}
                            </TableCell>
                            <TableCell>{error.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-4 w-4" />
                  Every row validated cleanly.
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mx-0 mt-0 shrink-0 gap-2 border-t bg-background px-6 py-3 sm:gap-0 sm:border-t sm:bg-background sm:px-6 sm:py-3">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={busy || !validation?.rows.length}
            className="min-w-[140px]"
          >
            {busy
              ? 'Importing…'
              : `Import ${validation?.rows.length ?? 0} Row(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CloudImportDialog;
